import { createSlice, nanoid } from '@reduxjs/toolkit';
import { errorMessageFor } from '@srichaitra/constants';

/**
 * Application-wide notifications — the success and error banners.
 *
 * A queue rather than a single slot, so two things finishing at once do not
 * silently overwrite one another. The shell renders them; any slice or
 * component can add one, which is what keeps "show the user what happened" out
 * of every individual screen.
 */

const initialState = {
  items: []
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    notify: {
      reducer(state, action) {
        state.items.push(action.payload);
      },
      /**
       * `prepare` generates the id, so callers dispatch notify({ tone, message })
       * and never have to invent one. Ids from nanoid rather than an index
       * because dismissing from the middle would otherwise reuse a key.
       */
      prepare({ tone = 'info', message, title = null }) {
        return { payload: { id: nanoid(), tone, message, title } };
      }
    },
    dismissNotification(state, action) {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    clearNotifications(state) {
      state.items = [];
    }
  }
});

export const { notify, dismissNotification, clearNotifications } = notificationSlice.actions;
export const notificationReducer = notificationSlice.reducer;

export const selectNotifications = (state) => state.notifications.items;

/* --- Convenience action creators --------------------------------------- */

export const notifySuccess = (message) => notify({ tone: 'success', message });

/**
 * Takes an error code or an ApiError and queues the right sentence for it, so
 * no call site has to translate a code itself.
 */
export const notifyError = (codeOrError) => notify({ tone: 'error', message: errorMessageFor(codeOrError) });
